import {ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, signal} from '@angular/core';
import {BzTableColumnSettings} from '@bringoz/ui';
import * as _ from 'lodash';
import {TimeCardModel} from '../../models/timecard.model';
import {TimeCardGroup} from '../../models/timecard-group.model';
import {resolveFieldValue} from '../../../utils/field.util';
import {
  TimekeepingColumnsMap,
  TimekeepingDateRange,
  TimekeepingSelectTimecardEvent
} from '../../models/timekeeping-list.model';

export interface FlattenedRow {
  rowId: string;
  type: 'parent' | 'child';
  group?: TimeCardGroup;
  timecard?: TimeCardModel;
  dayName?: string;
  isExpanded?: boolean;
  parentId?: string;
  index?: number;
  isLastChild?: boolean;
  date?: Date;
}

const SMALL_COLUMN_WIDTH = 80;
const COLUMN_WIDTH = 110;
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Initial width per column class, the bz-table column manager applies them and lets the user resize. */
export const TIMECARDS_TABLE_DEFAULT_WIDTHS: Record<string, number> = {
  'col-name': 220,
  'col-status': 200,
  'col-employeeId': COLUMN_WIDTH,
  'col-employeeZip': COLUMN_WIDTH,
  'col-jobTitle': COLUMN_WIDTH,
  'col-branch': COLUMN_WIDTH,
  'col-distance': SMALL_COLUMN_WIDTH,
  'col-mph': SMALL_COLUMN_WIDTH,
  'col-serviceTime': COLUMN_WIDTH,
  'col-driveTime': COLUMN_WIDTH,
  'col-adminTime': COLUMN_WIDTH,
  'col-pto': COLUMN_WIDTH,
  'col-paidTime': COLUMN_WIDTH,
  'col-plannedTime': COLUMN_WIDTH,
  'col-stops': SMALL_COLUMN_WIDTH,
  'col-break': COLUMN_WIDTH,
  'col-off': COLUMN_WIDTH
};

require('./timecards-table.scss');
@Component({
  standalone: false,
  selector: 'timecards-table',
  templateUrl: './timecards-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardsTableComponent {

  readonly timecardGroups = input<TimeCardGroup[]>([]);
  readonly dateRange = input<TimekeepingDateRange>();
  readonly loading = input<boolean>(false);
  readonly hasMoreData = input<boolean>(true);
  readonly columns = input<TimekeepingColumnsMap>({});
  readonly tableSettings = input<BzTableColumnSettings>();

  readonly timecardSelect = output<TimekeepingSelectTimecardEvent>();
  readonly loadMore = output<void>();
  readonly selectionChange = output<string[]>();
  readonly tableSettingsChange = output<BzTableColumnSettings>();

  readonly defaultWidths = TIMECARDS_TABLE_DEFAULT_WIDTHS;

  private readonly groups = computed(() => this.timecardGroups() || []);
  private readonly expandedGroupIds = signal<ReadonlySet<string>>(new Set());
  private readonly allTimecards = computed(() => _.flatMap(this.groups(), (group) => group.timeCards || []));

  // A new list of groups drops the selection.
  readonly selectedTimecardKeys = linkedSignal<TimeCardGroup[], Record<string, boolean>>({
    source: this.groups,
    computation: () => ({})
  });

  readonly flattenedRows = computed(() => buildFlattenedRows(this.groups(), this.expandedGroupIds(), this.dateRange()));

  readonly groupSelectedMap = computed(() => this.mapGroups((selectedCount, total) => total > 0 && selectedCount === total));
  readonly groupIndeterminateMap = computed(() => this.mapGroups((selectedCount, total) => selectedCount > 0 && selectedCount < total));

  private readonly selectedCount = computed(() => {
    const keys = this.selectedTimecardKeys();
    return _.filter(this.allTimecards(), (timecard) => !!keys[timecard.id]).length;
  });
  readonly allSelected = computed(() => this.allTimecards().length > 0 && this.selectedCount() === this.allTimecards().length);
  readonly headerIndeterminate = computed(() => this.selectedCount() > 0 && this.selectedCount() < this.allTimecards().length);

  onToggleSelectAll(): void {
    const isSelected = !this.allSelected();
    this.updateSelection((keys) => _.forEach(this.allTimecards(), (timecard) => {
      keys[timecard.id] = isSelected;
    }));
  }

  onToggleGroupSelect(group: TimeCardGroup): void {
    const isSelected = !this.groupSelectedMap()[group.id];
    this.updateSelection((keys) => _.forEach(group.timeCards, (timecard) => {
      keys[timecard.id] = isSelected;
    }));
  }

  onToggleTimecardSelect(timecard: TimeCardModel): void {
    this.updateSelection((keys) => {
      keys[timecard.id] = !keys[timecard.id];
    });
  }

  onSelectTimecard(timecard: TimeCardModel, group: TimeCardGroup, isAgentView: boolean, date?: Date): void {
    this.timecardSelect.emit({timecard, group, isAgentView, date});
  }

  onToggleExpand(group: TimeCardGroup): void {
    this.expandedGroupIds.update((expanded) => {
      const next = new Set(expanded);
      if (next.has(group.id)) {
        next.delete(group.id);
      } else {
        next.add(group.id);
      }
      return next;
    });
  }

  onFetchNext(): void {
    if (this.loading() || !this.hasMoreData()) {
      return;
    }
    this.loadMore.emit();
  }

  private updateSelection(mutate: (keys: Record<string, boolean>) => void): void {
    const keys = {...this.selectedTimecardKeys()};
    mutate(keys);
    this.selectedTimecardKeys.set(keys);
    this.selectionChange.emit(_.keys(_.pickBy(keys, _.identity)));
  }

  private mapGroups(isMatch: (selectedCount: number, total: number) => boolean): Record<string, boolean> {
    const keys = this.selectedTimecardKeys();
    return _.reduce(this.groups(), (result, group) => {
      const total = group?.timeCards?.length || 0;
      const selectedCount = _.filter(group?.timeCards, (timecard) => !!keys[timecard.id]).length;
      result[group.id] = isMatch(selectedCount, total);
      return result;
    }, {} as Record<string, boolean>);
  }
}

/**
 * One parent row per group (agent summary) and, when expanded, one child row per day of the range,
 * plus any day outside the range that still has a timecard.
 */
function buildFlattenedRows(groups: TimeCardGroup[], expandedGroupIds: ReadonlySet<string>, dateRange: TimekeepingDateRange): FlattenedRow[] {
  const rows: FlattenedRow[] = [];
  let globalIndex = 0;

  groups.forEach((group) => {
    const isExpanded = expandedGroupIds.has(group.id);
    rows.push({rowId: group.id, type: 'parent', group, isExpanded, index: globalIndex++});

    if (!isExpanded) {
      return;
    }

    let startTimestamp = dateRange?.dateStart || group.startTime;
    let endTimestamp = dateRange?.dateEnd || group.endTime;

    if (!startTimestamp) startTimestamp = Date.now();
    if (!endTimestamp) endTimestamp = startTimestamp;

    const startOfDayStamp = new Date(startTimestamp).setHours(0, 0, 0, 0);
    const endOfDayStamp = new Date(endTimestamp).setHours(0, 0, 0, 0);

    // Group timecards by local day stamp
    const timecardsByDay = new Map<number, TimeCardModel[]>();
    (group.timeCards || []).forEach((timecard) => {
      const clockIn = resolveFieldValue(timecard.clockInTimeField);
      const timecardTimestamp = clockIn ? new Date(clockIn).getTime() : group.startTime;
      const stamp = new Date(timecardTimestamp || Date.now()).setHours(0, 0, 0, 0);
      if (!timecardsByDay.has(stamp)) {
        timecardsByDay.set(stamp, []);
      }
      timecardsByDay.get(stamp).push(timecard);
    });

    // All unique stamps to render: range stamps + any extra stamps from timecards
    const stampsToRender = new Set<number>();
    let currentStamp = startOfDayStamp;
    while (currentStamp <= endOfDayStamp) {
      stampsToRender.add(currentStamp);
      const day = new Date(currentStamp);
      day.setDate(day.getDate() + 1);
      currentStamp = day.getTime();
    }
    for (const stamp of timecardsByDay.keys()) {
      stampsToRender.add(stamp);
    }

    const daysToRender: { stamp: number, timecard: TimeCardModel }[] = [];
    _.sortBy([...stampsToRender]).forEach((stamp) => {
      const timecards = timecardsByDay.get(stamp) || [];
      if (!timecards.length) {
        daysToRender.push({stamp, timecard: null});
        return;
      }
      _.sortBy(timecards, (timecard) => {
        const clockIn = resolveFieldValue(timecard.clockInTimeField);
        return clockIn ? new Date(clockIn).getTime() : 0;
      }).forEach((timecard) => daysToRender.push({stamp, timecard}));
    });

    daysToRender.forEach((dayRender, renderIndex) => {
      rows.push({
        rowId: `${group.id}-${dayRender.stamp}-${dayRender.timecard?.id || 'empty'}`,
        type: 'child',
        group,
        timecard: dayRender.timecard,
        date: new Date(dayRender.stamp),
        dayName: DAY_NAMES[new Date(dayRender.stamp).getDay()],
        parentId: group.id,
        index: globalIndex++,
        isLastChild: renderIndex === daysToRender.length - 1
      });
    });
  });

  return rows;
}
